import React, { useState, useEffect } from 'react';
import FacebookLogin from 'react-facebook-login';

import { useGoogleAnalyticsEventContext, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import NotificationError from '@/components/NotificationError';
import { GoogleLoginButton } from '@/components/SignIn/GoogleLoginButton';
import Svg from '@/components/Svg';

import { AppleLoginButton } from './AppleLoginButton';
import SignInInput from './controls/SignInInput';
import styles from './SignIn.module.scss';

export const FLOW_FULL = 'full';
export const FLOW_COMPACT = 'compact';

type settingsType = { appleClientId?: string; googleAppId?: string; facebookId?: string };

type SignInFields = {
  email: string;
  password: string;
  keepLoggedIn: boolean | string;
};

type signInType = {
  flow?: string;
  showErrorBox: boolean;
  settingData: settingsType;
  resourcesData: Record<string, any>;
  redirectURL: string;
  isSubmitDisabled: boolean;
  errorMsg: string[];
  onSignInFormFieldsChange: Function;
  handleSocialLogin: Function;
  onSignInSubmit: Function;
  signinFormState: SignInFields;
};

const SignIn = ({
  flow,
  showErrorBox,
  settingData,
  resourcesData,
  redirectURL,
  isSubmitDisabled,
  errorMsg,
  onSignInFormFieldsChange,
  handleSocialLogin,
  onSignInSubmit,
  signinFormState,
}: signInType) => {
  const defaultStrings = {
    googleBtn: 'Sign in with Google',
    facebookBtn: 'Sign in with Facebook',
    email: 'Email',
    pswd: 'Password',
    keeplogintext: 'Keep me logged in',
    signinButton: 'Sign in',
    forgotPwd: 'Forgot your password?',
    DontHaveAccount: "Don't have an account?",
    signUP: 'Sign Up',
    serverErrorMsg:
      "Looks like you've entered an incorrect email address and/or password (we get it, there are too many to remember these days). Please try again.",
  };
  const googleAnalyticsEventContext = useGoogleAnalyticsEventContext();
  const resourceString = isEmpty(resourcesData) ? defaultStrings : resourcesData;
  const { appleClientId, googleAppId, facebookId } = settingData;
  const [isVisible, setISVisible] = useState(false);
  const [formState, setFormState] = useState(signinFormState);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [inputBrowserErr, setInputBrowserErr] = useState<string | null>(null);

  useEffect(() => {
    if (showErrorBox) {
      const errorAsLabel =
        errorMsg.length > 0
          ? errorMsg.map((elem: any) => elem.toLowerCase().replace(/ /g, '-')).join(',')
          : resourceString.serverErrorMsg.toLowerCase().replace(/ /g, '-');
      googleAnalyticsEventContext.event(GoogleAnalyticsConstants.EVENT_ACTION.ERROR, `sign-in|${errorAsLabel}`, [], {
        element_type: 'error',
        element_title: errorAsLabel,
      });
    }
  }, [errorMsg, googleAnalyticsEventContext, resourceString, showErrorBox]);

  useEffect(() => {
    setFormState(signinFormState);
  }, [signinFormState]);

  const checkBoxHandler = (val: string) => {
    setFormState({ ...formState, keepLoggedIn: val });
    onSignInFormFieldsChange({ name: 'keepLoggedIn', value: val });
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const handleAutoFill = (value: boolean) => {
    setIsAutoFilled(value);
  };

  const passwordTypeHandler = () => {
    setISVisible(!isVisible);
  };

  const onInputEmailChange = (val: string, nativeErr: string | null) => {
    setInputBrowserErr(nativeErr);
    setFormState({ ...formState, email: val });
    onSignInFormFieldsChange({ name: 'email', value: val });
  };

  const onInputPasswordChange = (val: string) => {
    setFormState({ ...formState, password: val });
    onSignInFormFieldsChange({ name: 'password', value: val });
  };

  const onSubmitClick = () => {
    onSignInSubmit(formState);
  };

  const showPassIcon = !isVisible ? 'show' : 'hide';

  const handleFacebookLogin = (sdkResponse: any) => {
    const {
      first_name: firstname,
      last_name: lastname,
      id: socialMediaId,
      email,
      picture: { data },
      birthday,
    } = sdkResponse;

    const formFieldValues = {
      ...formState,
      firstname,
      lastname,
      birthday,
      email,
      socialMediaId,
      photo: data.url || '',
      type: 'facebook',
    };
    handleSocialLogin(formFieldValues);
  };

  const handleAppleLogin = (socialMediaId: string, user: any) => {
    const formFieldValues = {
      ...formState,
      socialMediaId,
      firstname: user?.name?.firstName,
      lastname: user?.name?.lastName,
      email: user?.email,
      type: 'apple',
    };
    handleSocialLogin(formFieldValues);
  };

  const trackInputBrowserError = () => {
    if (inputBrowserErr) {
      googleAnalyticsEventContext.event(
        GoogleAnalyticsConstants.EVENT_ACTION.ERROR,
        `sign-in|${inputBrowserErr.toLowerCase().replace(/ /g, '-')}`,
        [],
        {
          element_type: 'error',
          element_title: inputBrowserErr.toLowerCase().replace(/ /g, '-'),
        },
      );
    }
  };

  return (
    <div className="SignInContainer">
      <div className={styles.socialButtons}>
        <div className={classNames(styles.socialButtonWrapper, 'socialButtonWrapper')}>
          {googleAppId && (
            <GoogleLoginButton formState={formState} onLogin={handleSocialLogin}>
              {resourceString.googleBtn}
            </GoogleLoginButton>
          )}
        </div>
        <div className={classNames(styles.socialButtonWrapper, 'socialButtonWrapper')}>
          {facebookId && (
            <FacebookLogin
              appId={facebookId}
              autoLoad={false}
              redirectUri={redirectURL}
              fields="first_name, last_name, gender, picture, email, birthday"
              callback={handleFacebookLogin}
              onFailure={() => {}}
              cssClass={`${styles.socialButton} ${styles.facebookButton} socialButton facebookButton`}
              textButton="Sign in with Facebook"
              isMobile={false}
              scope="public_profile, email, user_birthday"
              icon={<Svg glyph="facebook" fill="#fff" width={20} height={20} iconId="facebook-btn" />}
            />
          )}
        </div>
        <div className={classNames(styles.socialButtonWrapper, 'socialButtonWrapper')}>
          {appleClientId && (
            <AppleLoginButton
              clientId={appleClientId}
              handleAppleLogin={handleAppleLogin}
              redirectURI={redirectURL}
              variant="SIGN_IN"
            />
          )}
        </div>
      </div>
      {flow !== FLOW_FULL && <div className={styles.separator}>— or —</div>}
      <div>
        {showErrorBox && (
          <NotificationError
            mainText="This page contains errors 🤦"
            subText={errorMsg.length > 0 ? errorMsg : resourceString.serverErrorMsg}
            icon
          />
        )}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitClick();
          }}
          name="signInForm"
        >
          <div className={styles.signin__fields}>
            <div className={styles.inputWrapper}>
              <SignInInput
                type="email"
                required
                name="email"
                value={formState.email}
                label={resourceString.email}
                onChange={onInputEmailChange}
                tabIndex={0}
                onClick={handleClick}
                onAutoFill={handleAutoFill}
              />
            </div>
            <div className={styles.inputWrapper}>
              <SignInInput
                required
                name="password"
                type={isVisible ? 'text' : 'password'}
                value={formState.password}
                label={resourceString.pswd}
                onChange={onInputPasswordChange}
                tabIndex={0}
                onClick={handleClick}
              />
              <div className={styles.showPasswordButtonWrapper}>
                <button
                  type="button"
                  aria-label="Show password"
                  className={styles.showPasswordButton}
                  onClick={passwordTypeHandler}
                >
                  <Svg
                    glyph={showPassIcon}
                    width={24}
                    height={24}
                    ariaLabel={showPassIcon}
                    iconId={showPassIcon}
                    fill="#8C8C8C"
                  />
                </button>
              </div>
            </div>
            <div className={styles.inputWrapper}>
              <Checkbox
                name="keepLoggedIn"
                label={resourceString.keeplogintext}
                onChange={checkBoxHandler}
                value={signinFormState?.keepLoggedIn}
              />
            </div>
            <div className={styles.signInButton}>
              <Button
                type="submit"
                variant="secondary"
                className="submitButton"
                disabled={isSubmitDisabled && !isAutoFilled}
                onClick={trackInputBrowserError}
              >
                {resourceString.signinButton}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
